import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { authAPI } from '../api/client';
import { useAuth, ROLE_LABELS } from '../context/AuthContext';
import { UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';

const getInitials = (name) =>
  name ? name.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

const AVATAR_COLORS = ['#4f46e5', '#16a34a', '#d97706', '#0891b2', '#dc2626'];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

export default function Profile() {
  const { fetchMe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.getProfile();
      setProfile(res.data);
      setFullName(res.data.full_name || '');
      setPhone(res.data.phone_number || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error('Full name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await authAPI.updateProfile({
        full_name: fullName.trim(),
        phone_number: phone.trim(),
      });
      setProfile(res.data);
      await fetchMe();           // refresh global user → header updates immediately
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await authAPI.changeMyPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!profile) return null;

  const avatarName = fullName || profile.full_name;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <UserCircleIcon className="w-6 h-6 text-primary-600" /> My Profile
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your personal details and password.</p>
      </div>

      {/* Identity card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center gap-5">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
          style={{ backgroundColor: getAvatarColor(avatarName) }}
        >
          {getInitials(avatarName)}
        </div>
        <div className="min-w-0">
          <p className="text-lg font-semibold text-slate-900 truncate">{avatarName}</p>
          <p className="text-sm text-slate-500 truncate">{profile.email}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary-50 text-primary-700">
              {ROLE_LABELS[profile.role] || profile.role}
            </span>
            {profile.department_name && (
              <span className="text-xs text-slate-400">{profile.department_name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Profile details */}
      <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800">Profile Details</h2>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Full Name</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
            Email <span className="font-normal text-slate-400 normal-case">(read-only)</span>
          </label>
          <input
            value={profile.email}
            disabled
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-400 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Phone Number</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 320 433 8568"
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={savingProfile}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50"
          >
            {savingProfile ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Change password */}
      <form onSubmit={savePassword} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <KeyIcon className="w-4 h-4 text-slate-500" /> Change Password
        </h2>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={4}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={4}
              autoComplete="new-password"
              className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                confirmPassword && confirmPassword !== newPassword
                  ? 'border-red-300 focus:ring-red-300'
                  : 'border-slate-200 focus:ring-primary-300'
              }`}
            />
          </div>
        </div>
        {confirmPassword && confirmPassword !== newPassword && (
          <p className="text-xs text-red-500">Passwords do not match.</p>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={savingPassword}
            className="px-4 py-2 text-sm bg-slate-800 text-white rounded-xl hover:bg-slate-900 disabled:opacity-50"
          >
            {savingPassword ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );
}
